import { Divider, InfoIcon } from '@wix/design-system';
import React, { useContext } from 'react';
import { RicosContext, ZIndexContext } from 'ricos-context';
import type { PollData } from 'ricos-schema/next';
import {
  PollData_Layout_PollLayout_Direction,
  PollData_Layout_PollLayout_Type,
} from 'ricos-schema/next';
import { LTRIcon, RTLIcon } from 'wix-rich-content-plugin-commons';
import { ThumbnailPicker, ToggleButton } from 'wix-rich-content-ui-components';

import { LayoutGridIcon, LayoutListIcon } from '../../assets/icons';
import {
  POLL_DIRECTION_LTR,
  POLL_DIRECTION_RTL,
  POLL_LAYOUT_GRID,
  POLL_LAYOUT_LIST,
  POLLS_IMAGE_TO_ANSWERS_TOGGLE,
  POLLS_IMAGE_TO_QUESTION_TOGGLE,
} from '../../dataHooks';
import { Section } from './section';

type LayoutSettingsSectionProps = {
  componentData: PollData;
  updateRicosDocument: (data: PollData) => void;
};

export const LayoutSettingsSection: React.FC<LayoutSettingsSectionProps> = ({
  updateRicosDocument,
  componentData,
}) => {
  const { t } = useContext(RicosContext);
  const zIndexService = useContext(ZIndexContext);
  const { poll, options } = componentData.layout ?? {};

  const updateSettings = layout => {
    updateRicosDocument({ layout: { ...componentData.layout, ...layout } });
  };

  const handlePollTypeChange = (type: PollData_Layout_PollLayout_Type) => {
    updateSettings({
      poll: { ...componentData.layout?.poll, type },
      option: {
        ...componentData.layout?.options,
        enableImage: type === PollData_Layout_PollLayout_Type.GRID,
      },
    });

    window.dispatchEvent(new Event('resize'));
  };

  const handleDirectionChange = (direction: PollData_Layout_PollLayout_Direction) => {
    updateSettings({
      poll: { ...componentData.layout?.poll, direction },
    });
  };

  return (
    <>
      <Section title={t('Poll_PollSettings_Tab_Layout_Section_Question_Header')}>
        <ToggleButton
          checked={Boolean(poll?.enableImage)}
          title={t('Poll_PollSettings_Tab_Layout_Section_Question_Image')}
          dataHook={POLLS_IMAGE_TO_QUESTION_TOGGLE}
          onChange={() => updateSettings({ poll: { ...poll, enableImage: !poll?.enableImage } })}
        />
      </Section>
      <Divider />
      <Section title={t('Poll_PollSettings_Tab_Layout_Section_Answers_Header')}>
        <ThumbnailPicker
          dataSource={[
            {
              dataHook: POLL_LAYOUT_LIST,
              value: PollData_Layout_PollLayout_Type.LIST,
              label: t('Poll_PollSettings_Tab_Layout_Section_Answers_Layout_List'),
              renderBackgroundImage: props => <LayoutListIcon {...props} />,
            },
            {
              dataHook: POLL_LAYOUT_GRID,
              value: PollData_Layout_PollLayout_Type.GRID,
              label: t('Poll_PollSettings_Tab_Layout_Section_Answers_Layout_Grid'),
              renderBackgroundImage: props => <LayoutGridIcon {...props} />,
            },
          ]}
          selectedValue={poll?.type ?? PollData_Layout_PollLayout_Type.LIST}
          title={t('Poll_PollSettings_Tab_Layout_Section_Answers_Layout')}
          onChange={handlePollTypeChange}
        />
        <ToggleButton
          checked={Boolean(options?.enableImage)}
          title={t('Poll_PollSettings_Tab_Layout_Section_Answers_Image')}
          dataHook={POLLS_IMAGE_TO_ANSWERS_TOGGLE}
          onChange={() =>
            updateSettings({ options: { ...options, enableImage: !options?.enableImage } })
          }
        />
      </Section>
      <Divider />
      <Section
        title={
          <>
            {t('Poll_PollSettings_Tab_Layout_Section_TextDirection_Header')}
            &nbsp;
            <InfoIcon
              content={t('Poll_PollSettings_Tab_Layout_Section_TextDirection_Header_Tooltip')}
              size="small"
              tooltipProps={{
                appendTo: el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal')),
                zIndex: zIndexService.getZIndex('TOOLTIP'),
              }}
            />
          </>
        }
      >
        <ThumbnailPicker
          dataSource={[
            {
              dataHook: POLL_DIRECTION_LTR,
              value: PollData_Layout_PollLayout_Direction.LTR,
              label: t('Poll_PollSettings_Tab_Layout_Section_TextDirection_LTR'),
              renderBackgroundImage: props => <LTRIcon {...props} />,
            },
            {
              dataHook: POLL_DIRECTION_RTL,
              value: PollData_Layout_PollLayout_Direction.RTL,
              label: t('Poll_PollSettings_Tab_Layout_Section_TextDirection_RTL'),
              renderBackgroundImage: props => <RTLIcon {...props} />,
            },
          ]}
          selectedValue={poll?.direction ?? PollData_Layout_PollLayout_Direction.LTR}
          title={t('Poll_PollSettings_Tab_Layout_Section_TextDirection_Choose')}
          onChange={handleDirectionChange}
        />
      </Section>
    </>
  );
};
